import { toast, ToastOptions } from 'react-toastify';
import { toastConstants } from '../constants/toast.constants';
import { AxiosError } from 'axios';
import { message } from 'antd';
import dayjs from 'dayjs';
import { getIntl } from '@umijs/max';

interface User {
  role: number;
  token: string;
}

export function isAdmin(): boolean {
  const userStr = localStorage.getItem('user');
  if (!userStr) return false;

  try {
    const user: User = JSON.parse(userStr);
    return user.role >= 10;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return false;
  }
}

export function isRoot(): boolean {
  const userStr = localStorage.getItem('user');
  if (!userStr) return false;

  try {
    const user: User = JSON.parse(userStr);
    return user.role >= 100;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return false;
  }
}

export function getSystemName(): string {
  const system_name = localStorage.getItem('system_name');
  if (!system_name) {
    return getIntl().formatMessage({ id: 'helpers.systemName.fallback', defaultMessage: '项目模板' });
  }
  return system_name;
}

export function getFooterHTML(): string | null {
  return localStorage.getItem('footer_html');
}

export async function copy(text: string): Promise<boolean> {
  let okay = true;
  try {
    await navigator.clipboard.writeText(text);
  } catch (e) {
    okay = false;
    console.error(e);
  }
  return okay;
}

export function isMobile(): boolean {
  return window.innerWidth <= 600;
}

const showErrorOptions: ToastOptions = { autoClose: toastConstants.ERROR_TIMEOUT };
const showWarningOptions: ToastOptions = { autoClose: toastConstants.WARNING_TIMEOUT };
const showSuccessOptions: ToastOptions = { autoClose: toastConstants.SUCCESS_TIMEOUT };
const showInfoOptions: ToastOptions = { autoClose: toastConstants.INFO_TIMEOUT };
const showNoticeOptions: ToastOptions = { autoClose: false };

if (isMobile()) {
  showErrorOptions.position = 'top-center';
  // showErrorOptions.transition = 'flip';

  showSuccessOptions.position = 'top-center';
  // showSuccessOptions.transition = 'flip';

  showInfoOptions.position = 'top-center';
  // showInfoOptions.transition = 'flip';

  showNoticeOptions.position = 'top-center';
  // showNoticeOptions.transition = 'flip';
}

export function showError(error: any): void {
  console.error(error);
  const intl = getIntl();
  const errPrefix = intl.formatMessage({ id: 'helpers.error.prefix', defaultMessage: '错误：' });
  if (error.message) {
    if (error.name === 'AxiosError' || error instanceof AxiosError) {
      const requestUrl: string =
        (error.config?.url as string) ||
        (error.request?.responseURL as string) ||
        '';
      const pathname = typeof window !== 'undefined' ? window.location?.pathname : '';
      const isLoginRequest =
        requestUrl.includes('/user/login') ||
        requestUrl.includes('/api/user/login') ||
        requestUrl.includes('/v1/auth/login');
      const isOnLoginPage = pathname === '/login';

      switch (error.response?.status) {
        case 401:
          // 登录接口的 401 表示“用户名或密码错误”，不应触发跳转刷新
          if (isLoginRequest || isOnLoginPage) {
            const serverMsg =
              error.response?.data?.message ||
              error.response?.data?.error ||
              error.message;
            message.error(
              serverMsg ||
                intl.formatMessage({ id: 'helpers.error.loginFailed', defaultMessage: '用户名或密码错误' }),
            );
            break;
          }
          message.error(
            intl.formatMessage({
              id: 'helpers.error.unauthorized',
              defaultMessage: '错误：未登录或登录已过期，请重新登录！',
            }),
          );
          window.location.href = '/login';
          break;
        case 429:
          message.error(
            intl.formatMessage({
              id: 'helpers.error.tooManyRequests',
              defaultMessage: '错误：请求次数过多，请稍后再试！',
            }),
          );
          break;
        case 500:
          message.error(
            error.response?.data?.message ||
              error.response?.data?.error ||
              intl.formatMessage({
                id: 'helpers.error.serverError',
                defaultMessage: '错误：服务器内部错误，请联系管理员！',
              }),
          );
          break;
        case 400:
          // 优先展示服务端返回的业务错误（统一信封 message/error），避免只显示 "Request failed with status code 400"
          message.error(
            error.response?.data?.message ||
              error.response?.data?.error ||
              errPrefix + error.message,
          );
          break;
        case 405:
          message.info(
            intl.formatMessage({
              id: 'helpers.error.demoOnly',
              defaultMessage: '本站仅作演示之用，无服务端！',
            }),
          );
          break;
        case 403:
          break;
        case 404:
          break;
        default:
          // 优先展示服务端返回的业务错误信息（统一信封的 message/error 字段），
          // 避免直接显示 "Request failed with status code 409" 这类原始 axios 信息
          message.error(
            error.response?.data?.message ||
              error.response?.data?.error ||
              errPrefix + error.message,
          );
      }
      return;
    }
    message.error(errPrefix + error.message);
  } else {
    message.error(errPrefix + error);
  }
}

export function showWarning(msg: string): void {
  message.warning(msg);
}

export function showSuccess(msg: string): void {
  message.success(msg);
}

export function showInfo(msg: string): void {
  message.info(msg);
}

export function showNotice(msg: string): void {
  message.info(msg);
}

export function openPage(url: string): void {
  window.open(url);
}

export function removeTrailingSlash(url: string): string {
  if (url.endsWith('/')) {
    return url.slice(0, -1);
  } else {
    return url;
  }
}

export function timestamp2string(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const year = date.getFullYear().toString();
  let month = (date.getMonth() + 1).toString();
  let day = date.getDate().toString();
  let hour = date.getHours().toString();
  let minute = date.getMinutes().toString();
  let second = date.getSeconds().toString();
  if (month.length === 1) {
    month = '0' + month;
  }
  if (day.length === 1) {
    day = '0' + day;
  }
  if (hour.length === 1) {
    hour = '0' + hour;
  }
  if (minute.length === 1) {
    minute = '0' + minute;
  }
  if (second.length === 1) {
    second = '0' + second;
  }
  return (
    year + '-' + month + '-' + day + ' ' + hour + ':' + minute + ':' + second
  );
}

/** 将后端 RFC3339/ISO8601（常为 UTC，带 Z）解析为浏览器本地时区后格式化为可读时间，避免表格直接展示 UTC 字符串造成「比库少 8 小时」观感。 */
export function formatApiDateTime(iso: string | undefined | null): string {
  if (iso == null || String(iso).trim() === '') {
    return '—';
  }
  const d = dayjs(iso);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : String(iso);
}
